import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@vns/core';
import { supabase } from './supabase';
import { cleanDisplayRemarks, cleanWorkTypeName } from './transactions';
import type { ExportColumn } from './export';
import type { IconName } from '../components/ui';

/**
 * The single-project drill-down report: one project, every ledger, in one
 * place — the prototype's Project Report screen.
 *
 * This deliberately does not re-query what `lib/project.ts` already fetches
 * for ProjectDetail: `useProject` and six calls to `useLedger` (one per tab)
 * give every row this report needs, already joined to names, and already
 * cached under the same query keys ProjectDetail uses — opening this report
 * for a project already viewed today costs no extra round trip. Everything
 * here is pure aggregation of those rows into the sections the prototype
 * shows, plus the filter-list query the picker needs.
 */

function unwrap<T>({ data, error }: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (error) throw new Error(error.message);
  if (data === null || data === undefined) throw new Error('No data returned');
  return data as NonNullable<T>;
}

const rel = (v: unknown, k = 'name') => (v as Record<string, string> | null)?.[k] ?? '—';
const day = (iso: unknown) =>
  iso ? new Date(`${String(iso)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const num = (n: unknown) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const title = (s: unknown) => String(s).charAt(0).toUpperCase() + String(s).slice(1).replace(/_/g, ' ');

type Row = Record<string, unknown>;

/* --------------------------------- picker --------------------------------- */

export interface ProjectPickerRow {
  id: string;
  name: string;
  location: string | null;
  status: string;
  clientId: string;
  clientName: string;
}

/** Every project, for the report's own Client → Status → Location → Project
 *  filter chain. A dedicated, lightweight query rather than `useLookups()`'s
 *  `projects` list, which carries no client or location to filter by. */
export function useProjectPickerList() {
  return useQuery({
    queryKey: ['project-report-picker'],
    queryFn: async (): Promise<ProjectPickerRow[]> => {
      const rows = unwrap(
        await supabase
          .from('projects')
          .select('id, name, location, status, client_id, clients(name)')
          .is('deleted_at', null)
          .order('name'),
      );
      return rows.map((r) => ({
        id: r.id,
        name: r.name,
        location: r.location,
        status: r.status,
        clientId: r.client_id,
        clientName: rel(r.clients),
      }));
    },
  });
}

/* -------------------------------- sections -------------------------------- */

export interface ReportSection {
  key: string;
  title: string;
  icon: IconName;
  columns: ExportColumn[];
  rows: Row[];
}

/** Distinct, ordered, comma-joined — good enough for "who supplied this
 *  material" without a second query for the full list. */
function distinctJoin(names: Iterable<string>, max = 3): string {
  const list = Array.from(new Set(names)).filter((n) => n && n !== '—');
  if (!list.length) return '—';
  return list.length > max ? `${list.slice(0, max).join(', ')} +${list.length - max} more` : list.join(', ');
}

export function paymentsSection(rows: Row[]): ReportSection {
  return {
    key: 'payments', title: 'Client Payment History', icon: 'wallet',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' }, { key: 'type', label: 'Type' },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
      { key: 'reference', label: 'Reference' }, { key: 'remarks', label: 'Remarks' },
    ],
    rows: rows
      .map((r) => ({
        date: day(r.paid_on), _sort: String(r.paid_on ?? ''),
        type: title(String(r.payment_type).replace('_', ' ')),
        amount: formatMoney(Number(r.amount_paise)), _amount: Number(r.amount_paise),
        reference: (r.reference_no as string) || '—',
        remarks: cleanDisplayRemarks(r.remarks as string) || '—',
      }))
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function materialSummarySection(purchases: Row[]): ReportSection {
  const byMaterial = new Map<string, { materialId: string; unit: string; qty: number; amount: number; suppliers: Set<string>; latestDate: string }>();
  for (const r of purchases) {
    const name = rel(r.materials);
    const materialId = (r.materials as unknown as { id?: string } | null)?.id ?? (r.material_id as string) ?? '';
    const cur = byMaterial.get(name) ?? { materialId, unit: rel(r.units, 'short_name'), qty: 0, amount: 0, suppliers: new Set<string>(), latestDate: '' };
    cur.qty += Number(r.quantity);
    cur.amount += Number(r.amount_paise);
    cur.suppliers.add(rel(r.suppliers));
    if (r.purchased_on && String(r.purchased_on) > cur.latestDate) cur.latestDate = String(r.purchased_on);
    if (materialId && !cur.materialId) cur.materialId = materialId;
    byMaterial.set(name, cur);
  }
  const rows = Array.from(byMaterial.entries())
    .map(([material, v]) => ({
      date: day(v.latestDate || new Date().toISOString().slice(0, 10)),
      _materialId: v.materialId,
      material, unit: v.unit, quantity: num(v.qty), _quantity: v.qty,
      amount: formatMoney(v.amount), _amount: v.amount,
      avgRate: formatMoney(v.qty ? Math.round(v.amount / v.qty) : 0),
      suppliers: distinctJoin(v.suppliers),
    }))
    .sort((a, b) => b._amount - a._amount)
    .map((r, i) => ({ sno: i + 1, ...r }));
  return {
    key: 'materials', title: 'Material Summary', icon: 'box',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'material', label: 'Material' }, { key: 'unit', label: 'Unit' },
      { key: 'quantity', label: 'Purchased', numeric: true },
      { key: 'avgRate', label: 'Avg. rate', numeric: true, money: true },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
      { key: 'suppliers', label: 'Suppliers' },
    ],
    rows,
  };
}

export function workerSummarySection(labour: Row[]): ReportSection {
  const byWorkType = new Map<string, { workers: number; amount: number; subs: Set<string>; latestDate: string }>();
  for (const r of labour) {
    const name = cleanWorkTypeName(rel(r.work_types));
    const cur = byWorkType.get(name) ?? { workers: 0, amount: 0, subs: new Set<string>(), latestDate: '' };
    cur.workers += Number(r.worker_count);
    cur.amount += Number(r.total_amount_paise);
    cur.subs.add(rel(r.subcontractors));
    if (r.worked_on && String(r.worked_on) > cur.latestDate) cur.latestDate = String(r.worked_on);
    byWorkType.set(name, cur);
  }
  const rows = Array.from(byWorkType.entries())
    .map(([workType, v]) => ({
      date: day(v.latestDate || new Date().toISOString().slice(0, 10)),
      workType, workers: v.workers, _workers: v.workers,
      subcontractors: distinctJoin(v.subs),
      amount: formatMoney(v.amount), _amount: v.amount,
    }))
    .sort((a, b) => b._amount - a._amount)
    .map((r, i) => ({ sno: i + 1, ...r }));
  return {
    key: 'workers', title: 'Worker Summary', icon: 'hardhat',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'workType', label: 'Work type' },
      { key: 'workers', label: 'Worker-days', numeric: true },
      { key: 'subcontractors', label: 'Sub-contractors' },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
    ],
    rows,
  };
}

export function supplierSummarySection(purchases: Row[]): ReportSection {
  const bySupplier = new Map<string, { orders: number; amount: number; advance: number; latestDate: string }>();
  for (const r of purchases) {
    const name = rel(r.suppliers);
    const cur = bySupplier.get(name) ?? { orders: 0, amount: 0, advance: 0, latestDate: '' };
    cur.orders += 1;
    cur.amount += Number(r.amount_paise);
    cur.advance += Number(r.advance_paid_paise);
    if (r.purchased_on && String(r.purchased_on) > cur.latestDate) cur.latestDate = String(r.purchased_on);
    bySupplier.set(name, cur);
  }
  const rows = Array.from(bySupplier.entries())
    .map(([supplier, v]) => ({
      date: day(v.latestDate || new Date().toISOString().slice(0, 10)),
      supplier, orders: v.orders,
      value: formatMoney(v.amount), _value: v.amount,
      paid: formatMoney(v.advance), _paid: v.advance,
      balance: formatMoney(Math.max(0, v.amount - v.advance)), _balance: Math.max(0, v.amount - v.advance),
    }))
    .sort((a, b) => b._value - a._value)
    .map((r, i) => ({ sno: i + 1, ...r }));
  return {
    key: 'suppliers', title: 'Supplier Summary', icon: 'truck',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'supplier', label: 'Supplier' }, { key: 'orders', label: 'Purchases', numeric: true },
      { key: 'value', label: 'Value', numeric: true, money: true },
      { key: 'paid', label: 'Advance paid', numeric: true, money: true },
      { key: 'balance', label: 'Balance', numeric: true, money: true },
    ],
    rows,
  };
}

export function subcontractorSummarySection(labour: Row[]): ReportSection {
  const bySub = new Map<string, { workers: number; amount: number; types: Set<string>; latestDate: string }>();
  for (const r of labour) {
    const name = rel(r.subcontractors);
    const cur = bySub.get(name) ?? { workers: 0, amount: 0, types: new Set<string>(), latestDate: '' };
    cur.workers += Number(r.worker_count);
    cur.amount += Number(r.total_amount_paise);
    cur.types.add(cleanWorkTypeName(rel(r.work_types)));
    if (r.worked_on && String(r.worked_on) > cur.latestDate) cur.latestDate = String(r.worked_on);
    bySub.set(name, cur);
  }
  const rows = Array.from(bySub.entries())
    .map(([subcontractor, v]) => ({
      date: day(v.latestDate || new Date().toISOString().slice(0, 10)),
      subcontractor, workTypes: distinctJoin(v.types),
      workers: v.workers, _workers: v.workers,
      amount: formatMoney(v.amount), _amount: v.amount,
    }))
    .sort((a, b) => b._amount - a._amount)
    .map((r, i) => ({ sno: i + 1, ...r }));
  return {
    key: 'subcontractors', title: 'Sub Contractor Summary', icon: 'hardhat',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'subcontractor', label: 'Sub-contractor' }, { key: 'workTypes', label: 'Work types' },
      { key: 'workers', label: 'Worker-days', numeric: true },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
    ],
    rows,
  };
}

export function expenseSummarySection(expenses: Row[]): ReportSection {
  const byCategory = new Map<string, { count: number; amount: number; latestDate: string }>();
  for (const r of expenses) {
    const name = rel(r.expense_categories);
    const key = name === '—' ? 'Uncategorised' : name;
    const cur = byCategory.get(key) ?? { count: 0, amount: 0, latestDate: '' };
    cur.count += 1;
    cur.amount += Number(r.amount_paise);
    if (r.spent_on && String(r.spent_on) > cur.latestDate) cur.latestDate = String(r.spent_on);
    byCategory.set(key, cur);
  }
  const rows = Array.from(byCategory.entries())
    .map(([category, v]) => ({
      date: day(v.latestDate || new Date().toISOString().slice(0, 10)),
      category, count: v.count,
      amount: formatMoney(v.amount), _amount: v.amount,
    }))
    .sort((a, b) => b._amount - a._amount)
    .map((r, i) => ({ sno: i + 1, ...r }));
  return {
    key: 'expenses', title: 'Expense Summary', icon: 'wallet',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'category', label: 'Category' }, { key: 'count', label: 'Entries', numeric: true },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
    ],
    rows,
  };
}

export function transferSection(rows: Row[], projectId: string): ReportSection {
  return {
    key: 'transfers', title: 'Site Transfer Summary', icon: 'repeat',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' }, { key: 'direction', label: 'Direction' },
      { key: 'material', label: 'Material' }, { key: 'quantity', label: 'Qty', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'rate', label: 'Rate', numeric: true, money: true },
      { key: 'amount', label: 'Total Value', numeric: true, money: true },
      { key: 'party', label: 'From / To' }, { key: 'vehicle', label: 'Vehicle' },
    ],
    rows: rows
      .map((r) => {
        const outgoing = r.from_project_id === projectId;
        const materialId = (r.materials as unknown as { id?: string } | null)?.id ?? (r.material_id as string) ?? '';
        const ratePaise = r.rate_paise != null ? Number(r.rate_paise) : null;
        const amountPaise = r.amount_paise != null ? Number(r.amount_paise) : null;
        return {
          _materialId: materialId,
          date: day(r.transferred_on), _sort: String(r.transferred_on ?? ''),
          direction: outgoing ? 'Sent out' : 'Received',
          material: rel(r.materials), quantity: num(r.quantity), _quantity: Number(r.quantity),
          unit: rel(r.units, 'short_name'),
          rate: ratePaise != null ? formatMoney(ratePaise) : '—',
          amount: amountPaise != null ? formatMoney(amountPaise) : '—',
          _amount: amountPaise ?? 0,
          party: outgoing ? rel(r.to_project) : rel(r.from_project),
          vehicle: (r.vehicle_no as string) || '—',
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function stockSummarySection(purchases: Row[], usage: Row[]): ReportSection {
  const byMaterial = new Map<string, { materialId: string; unit: string; purchased: number; used: number; amount: number; latestDate: string }>();
  for (const r of purchases) {
    const name = rel(r.materials);
    const materialId = (r.materials as unknown as { id?: string } | null)?.id ?? (r.material_id as string) ?? '';
    const cur = byMaterial.get(name) ?? { materialId, unit: rel(r.units, 'short_name'), purchased: 0, used: 0, amount: 0, latestDate: '' };
    cur.purchased += Number(r.quantity);
    cur.amount += Number(r.amount_paise);
    if (r.purchased_on && String(r.purchased_on) > cur.latestDate) cur.latestDate = String(r.purchased_on);
    if (materialId && !cur.materialId) cur.materialId = materialId;
    byMaterial.set(name, cur);
  }
  for (const r of usage) {
    const name = rel(r.materials);
    const materialId = (r.materials as unknown as { id?: string } | null)?.id ?? (r.material_id as string) ?? '';
    const cur = byMaterial.get(name) ?? { materialId, unit: rel(r.units, 'short_name'), purchased: 0, used: 0, amount: 0, latestDate: '' };
    cur.used += Number(r.quantity);
    if (r.used_on && String(r.used_on) > cur.latestDate) cur.latestDate = String(r.used_on);
    if (materialId && !cur.materialId) cur.materialId = materialId;
    byMaterial.set(name, cur);
  }
  const rows = Array.from(byMaterial.entries())
    .map(([material, v]) => {
      const available = v.purchased - v.used;
      // Valued at this project's own average purchase rate, not the
      // material's current catalogue rate — what this project actually paid,
      // not a figure that may have moved since.
      const avgRate = v.purchased ? v.amount / v.purchased : 0;
      // `+ 0` folds a stray -0 (available negative x rate 0) back to 0 —
      // material used here but never purchased here, e.g. brought in by a
      // stock transfer instead, must read as ₹0, not "-₹0".
      const value = Math.round(available * avgRate) + 0;
      return {
        date: day(v.latestDate || new Date().toISOString().slice(0, 10)),
        _materialId: v.materialId,
        material, unit: v.unit,
        purchased: num(v.purchased), used: num(v.used), available: num(available),
        value: formatMoney(value), _value: value,
      };
    })
    .sort((a, b) => b._value - a._value)
    .map((r, i) => ({ sno: i + 1, ...r }));
  return {
    key: 'stock', title: 'Stock Summary', icon: 'box',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'material', label: 'Material' }, { key: 'unit', label: 'Unit' },
      { key: 'purchased', label: 'Purchased', numeric: true }, { key: 'used', label: 'Used', numeric: true },
      { key: 'available', label: 'Available', numeric: true },
      { key: 'value', label: 'Value of stock', numeric: true, money: true },
    ],
    rows,
  };
}

export interface ProjectFinancials {
  valuePaise: number;
  paidPaise: number;
  pendingPaise: number;
}

export function financialSummarySection(
  project: ProjectFinancials, purchases: Row[], labour: Row[], expenses: Row[],
): ReportSection {
  const materialCost = purchases.reduce((s, r) => s + Number(r.amount_paise), 0);
  const advancePaid = purchases.reduce((s, r) => s + Number(r.advance_paid_paise), 0);
  const labourCost = labour.reduce((s, r) => s + Number(r.total_amount_paise), 0);
  const expenseCost = expenses.reduce((s, r) => s + Number(r.amount_paise), 0);
  const spent = materialCost + labourCost + expenseCost;

  const line = (item: string, amount: number) => ({ item, amount: formatMoney(amount), _amount: amount });
  const rawRows = [
    line('Project value', project.valuePaise),
    line('Received from client', project.paidPaise),
    line('Pending from client', project.pendingPaise),
    line('Material purchases', materialCost),
    line('Advance paid to suppliers', advancePaid),
    line('Outstanding to suppliers', Math.max(0, materialCost - advancePaid)),
    line('Labour cost', labourCost),
    line('Site expenses', expenseCost),
    line('Total spent', spent),
    line('Net position (received − spent)', project.paidPaise - spent),
  ];
  return {
    key: 'financial', title: 'Financial Summary', icon: 'wallet',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'item', label: 'Item' },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
    ],
    rows: rawRows.map((r, i) => ({ sno: i + 1, ...r })),
  };
}

/* ------------------------------- All Projects (Consolidated) ------------------------------- */

export interface AllProjectsReportData {
  projects: Array<{
    id: string;
    name: string;
    location: string | null;
    status: string;
    client_id: string;
    value_paise: number;
    start_date: string | null;
    clients: { name: string } | null;
  }>;
  payments: Row[];
  purchases: Row[];
  usage: Row[];
  labour: Row[];
  expenses: Row[];
  transfers: Row[];
}

export function useAllProjectsReportData(enabled: boolean) {
  return useQuery({
    queryKey: ['all-projects-report-data'],
    enabled,
    queryFn: async (): Promise<AllProjectsReportData> => {
      const [projects, payments, purchases, usage, labour, expenses, transfers] = await Promise.all([
        unwrap(
          await supabase
            .from('projects')
            .select('id, name, location, status, client_id, value_paise, start_date, clients(name)')
            .is('deleted_at', null)
            .order('name'),
        ),
        unwrap(
          await supabase
            .from('client_payments')
            .select('id, project_id, paid_on, amount_paise, payment_type, reference_no, remarks, projects(id, name, clients(name))')
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('paid_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('material_purchases')
            .select('id, project_id, purchased_on, quantity, rate_paise, amount_paise, advance_paid_paise, invoice_no, material_id, supplier_id, materials(id, name), suppliers(id, name), units(short_name), projects(id, name)')
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('purchased_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('material_usage')
            .select('id, project_id, used_on, quantity, remarks, material_id, materials(id, name), units(short_name), projects(id, name)')
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('used_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('subcontractor_labour')
            .select('id, project_id, worked_on, worker_count, total_amount_paise, rate_per_worker_paise, work_type_id, subcontractor_id, work_types(name), subcontractors(name), projects(id, name)')
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('worked_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('expenses')
            .select('id, project_id, spent_on, amount_paise, title, paid_to, bill_no, remarks, category_id, expense_categories(name), projects(id, name)')
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('spent_on', { ascending: false }),
        ),
        unwrap(
          await supabase
            .from('stock_transfers')
            .select('id, transferred_on, quantity, vehicle_no, driver_name, remarks, material_id, from_project_id, to_project_id, materials(id, name), units(short_name), from_project:projects!stock_transfers_from_project_id_fkey(id, name), to_project:projects!stock_transfers_to_project_id_fkey(id, name)')
            .eq('approval_status', 'approved')
            .is('deleted_at', null)
            .order('transferred_on', { ascending: false }),
        ),
      ]);

      return {
        projects: projects as AllProjectsReportData['projects'],
        payments,
        purchases,
        usage,
        labour,
        expenses,
        transfers,
      };
    },
  });
}

export function allProjectsOverviewSection(
  projects: AllProjectsReportData['projects'],
  payments: Row[],
  purchases: Row[],
  labour: Row[],
  expenses: Row[],
): ReportSection {
  const paidMap = new Map<string, number>();
  for (const r of payments) {
    const pid = String(r.project_id || '');
    paidMap.set(pid, (paidMap.get(pid) ?? 0) + Number(r.amount_paise));
  }

  const matMap = new Map<string, number>();
  for (const r of purchases) {
    const pid = String(r.project_id || '');
    matMap.set(pid, (matMap.get(pid) ?? 0) + Number(r.amount_paise));
  }

  const labMap = new Map<string, number>();
  for (const r of labour) {
    const pid = String(r.project_id || '');
    labMap.set(pid, (labMap.get(pid) ?? 0) + Number(r.total_amount_paise));
  }

  const expMap = new Map<string, number>();
  for (const r of expenses) {
    const pid = String(r.project_id || '');
    expMap.set(pid, (expMap.get(pid) ?? 0) + Number(r.amount_paise));
  }

  const rows = projects.map((p, idx) => {
    const budget = Number(p.value_paise ?? 0);
    const received = paidMap.get(p.id) ?? 0;
    const mat = matMap.get(p.id) ?? 0;
    const lab = labMap.get(p.id) ?? 0;
    const exp = expMap.get(p.id) ?? 0;
    const spent = mat + lab + exp;
    const net = received - spent;
    const pending = Math.max(0, budget - received);

    return {
      sno: idx + 1,
      date: day(p.start_date),
      _projectId: p.id,
      project: p.name,
      client: rel(p.clients),
      status: title(p.status),
      budget: formatMoney(budget),
      _budget: budget,
      received: formatMoney(received),
      _received: received,
      spent: formatMoney(spent),
      _spent: spent,
      net: formatMoney(net),
      _net: net,
      pending: formatMoney(pending),
      _pending: pending,
    };
  }).sort((a, b) => b._budget - a._budget).map((r, i) => ({ ...r, sno: i + 1 }));

  return {
    key: 'allProjectsOverview',
    title: 'Projects Portfolio & Performance Summary',
    icon: 'briefcase',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Start Date' },
      { key: 'project', label: 'Project' },
      { key: 'client', label: 'Client' },
      { key: 'status', label: 'Status' },
      { key: 'budget', label: 'Project Value', numeric: true, money: true },
      { key: 'received', label: 'Received', numeric: true, money: true },
      { key: 'spent', label: 'Total Spent', numeric: true, money: true },
      { key: 'net', label: 'Net Position', numeric: true, money: true },
      { key: 'pending', label: 'Pending Due', numeric: true, money: true },
    ],
    rows,
  };
}

export function allProjectsPaymentsSection(rows: Row[]): ReportSection {
  return {
    key: 'allPayments',
    title: 'Client Payment Collections',
    icon: 'wallet',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project' },
      { key: 'client', label: 'Client' },
      { key: 'type', label: 'Type' },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
      { key: 'reference', label: 'Reference' },
      { key: 'remarks', label: 'Remarks' },
    ],
    rows: rows
      .map((r) => {
        const proj = r.projects as { name?: string; clients?: { name?: string } | null } | null;
        return {
          date: day(r.paid_on),
          _sort: String(r.paid_on ?? ''),
          project: proj?.name ?? '—',
          client: proj?.clients?.name ?? '—',
          type: title(String(r.payment_type).replace('_', ' ')),
          amount: formatMoney(Number(r.amount_paise)),
          _amount: Number(r.amount_paise),
          reference: (r.reference_no as string) || '—',
          remarks: cleanDisplayRemarks(r.remarks as string) || '—',
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function allProjectsMaterialsSection(rows: Row[]): ReportSection {
  return {
    key: 'allMaterials',
    title: 'Material Purchases & Invoices',
    icon: 'truck',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project' },
      { key: 'material', label: 'Material' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'invoice', label: 'Invoice No.' },
      { key: 'quantity', label: 'Quantity', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'rate', label: 'Rate', numeric: true, money: true },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
      { key: 'paid', label: 'Advance Paid', numeric: true, money: true },
      { key: 'balance', label: 'Balance Due', numeric: true, money: true },
    ],
    rows: rows
      .map((r) => {
        const materialId = (r.materials as unknown as { id?: string } | null)?.id ?? (r.material_id as string) ?? '';
        const qtyVal = Number(r.quantity);
        const amount = Number(r.amount_paise);
        const advance = Number(r.advance_paid_paise ?? 0);
        return {
          _materialId: materialId,
          date: day(r.purchased_on),
          _sort: String(r.purchased_on ?? ''),
          project: rel(r.projects),
          material: rel(r.materials),
          supplier: rel(r.suppliers),
          invoice: (r.invoice_no as string) || '—',
          quantity: num(qtyVal),
          _quantity: qtyVal,
          unit: rel(r.units, 'short_name'),
          rate: formatMoney(Number(r.rate_paise)),
          amount: formatMoney(amount),
          _amount: amount,
          paid: formatMoney(advance),
          _paid: advance,
          balance: formatMoney(Math.max(0, amount - advance)),
          _balance: Math.max(0, amount - advance),
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function allProjectsUsageSection(rows: Row[]): ReportSection {
  return {
    key: 'allUsage',
    title: 'Site Material Consumption Logs',
    icon: 'box',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project' },
      { key: 'material', label: 'Material' },
      { key: 'quantity', label: 'Quantity Used', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'remarks', label: 'Remarks / Notes' },
    ],
    rows: rows
      .map((r) => {
        const materialId = (r.materials as unknown as { id?: string } | null)?.id ?? (r.material_id as string) ?? '';
        const qtyVal = Number(r.quantity);
        return {
          _materialId: materialId,
          date: day(r.used_on),
          _sort: String(r.used_on ?? ''),
          project: rel(r.projects),
          material: rel(r.materials),
          quantity: num(qtyVal),
          _quantity: qtyVal,
          unit: rel(r.units, 'short_name'),
          remarks: cleanDisplayRemarks(r.remarks as string) || '—',
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function allProjectsLabourSection(rows: Row[]): ReportSection {
  return {
    key: 'allLabour',
    title: 'Labour & Workforce Deployments',
    icon: 'hardhat',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project' },
      { key: 'workType', label: 'Work Type' },
      { key: 'subcontractor', label: 'Subcontractor' },
      { key: 'workers', label: 'Worker-days', numeric: true },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
    ],
    rows: rows
      .map((r) => {
        const workers = Number(r.worker_count);
        const amount = Number(r.total_amount_paise);
        return {
          date: day(r.worked_on),
          _sort: String(r.worked_on ?? ''),
          project: rel(r.projects),
          workType: cleanWorkTypeName(rel(r.work_types)),
          subcontractor: rel(r.subcontractors),
          workers,
          _workers: workers,
          amount: formatMoney(amount),
          _amount: amount,
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function allProjectsSubcontractorsSection(labour: Row[]): ReportSection {
  const bySub = new Map<string, { workers: number; amount: number; types: Set<string>; projects: Set<string> }>();
  for (const r of labour) {
    const name = rel(r.subcontractors);
    const cur = bySub.get(name) ?? { workers: 0, amount: 0, types: new Set<string>(), projects: new Set<string>() };
    cur.workers += Number(r.worker_count);
    cur.amount += Number(r.total_amount_paise);
    cur.types.add(cleanWorkTypeName(rel(r.work_types)));
    cur.projects.add(rel(r.projects));
    bySub.set(name, cur);
  }
  const rows = Array.from(bySub.entries())
    .map(([subcontractor, v], idx) => ({
      sno: idx + 1,
      date: day(new Date()),
      subcontractor,
      projects: distinctJoin(v.projects),
      workTypes: distinctJoin(v.types),
      workers: v.workers,
      _workers: v.workers,
      amount: formatMoney(v.amount),
      _amount: v.amount,
    }))
    .sort((a, b) => b._amount - a._amount)
    .map((r, i) => ({ ...r, sno: i + 1 }));

  return {
    key: 'allSubcontractors',
    title: 'Subcontractor Summary & Project Breakdown',
    icon: 'users',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'subcontractor', label: 'Subcontractor' },
      { key: 'projects', label: 'Projects Engaged' },
      { key: 'workTypes', label: 'Work Types' },
      { key: 'workers', label: 'Worker-days', numeric: true },
      { key: 'amount', label: 'Total Amount', numeric: true, money: true },
    ],
    rows,
  };
}

export function allProjectsSuppliersSection(purchases: Row[]): ReportSection {
  const bySupplier = new Map<string, { orders: number; amount: number; advance: number; projects: Set<string> }>();
  for (const r of purchases) {
    const name = rel(r.suppliers);
    const cur = bySupplier.get(name) ?? { orders: 0, amount: 0, advance: 0, projects: new Set<string>() };
    cur.orders += 1;
    cur.amount += Number(r.amount_paise);
    cur.advance += Number(r.advance_paid_paise ?? 0);
    cur.projects.add(rel(r.projects));
    bySupplier.set(name, cur);
  }
  const rows = Array.from(bySupplier.entries())
    .map(([supplier, v], idx) => ({
      sno: idx + 1,
      date: day(new Date()),
      supplier,
      projects: distinctJoin(v.projects),
      orders: v.orders,
      _orders: v.orders,
      value: formatMoney(v.amount),
      _value: v.amount,
      paid: formatMoney(v.advance),
      _paid: v.advance,
      balance: formatMoney(Math.max(0, v.amount - v.advance)),
      _balance: Math.max(0, v.amount - v.advance),
    }))
    .sort((a, b) => b._value - a._value)
    .map((r, i) => ({ ...r, sno: i + 1 }));

  return {
    key: 'allSuppliers',
    title: 'Supplier Summary & Outstanding Balances',
    icon: 'truck',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'projects', label: 'Projects Supplied' },
      { key: 'orders', label: 'Invoices / Orders', numeric: true },
      { key: 'value', label: 'Total Purchase Value', numeric: true, money: true },
      { key: 'paid', label: 'Advance Paid', numeric: true, money: true },
      { key: 'balance', label: 'Outstanding Balance', numeric: true, money: true },
    ],
    rows,
  };
}

export function allProjectsExpensesSection(expenses: Row[]): ReportSection {
  return {
    key: 'allExpenses',
    title: 'Site Petty Cash & Expenses Log',
    icon: 'wallet',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project' },
      { key: 'category', label: 'Category' },
      { key: 'description', label: 'Description' },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
    ],
    rows: expenses
      .map((r) => {
        const amount = Number(r.amount_paise);
        return {
          date: day(r.spent_on),
          _sort: String(r.spent_on ?? ''),
          project: rel(r.projects),
          category: rel(r.expense_categories, 'name') || 'Uncategorised',
          description: (r.title as string) || (r.description as string) || cleanDisplayRemarks(r.remarks as string) || '—',
          amount: formatMoney(amount),
          _amount: amount,
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function allProjectsTransfersSection(rows: Row[]): ReportSection {
  return {
    key: 'allTransfers',
    title: 'Inter-Project Stock Transfers',
    icon: 'repeat',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'material', label: 'Material' },
      { key: 'quantity', label: 'Quantity', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'fromProject', label: 'From Project' },
      { key: 'toProject', label: 'To Project' },
      { key: 'vehicle', label: 'Vehicle No.' },
      { key: 'driver', label: 'Driver' },
      { key: 'remarks', label: 'Remarks' },
    ],
    rows: rows
      .map((r) => {
        const materialId = (r.materials as unknown as { id?: string } | null)?.id ?? (r.material_id as string) ?? '';
        const qtyVal = Number(r.quantity);
        return {
          _materialId: materialId,
          date: day(r.transferred_on),
          _sort: String(r.transferred_on ?? ''),
          material: rel(r.materials),
          quantity: num(qtyVal),
          _quantity: qtyVal,
          unit: rel(r.units, 'short_name'),
          fromProject: rel(r.from_project),
          toProject: rel(r.to_project),
          vehicle: (r.vehicle_no as string) || '—',
          driver: (r.driver_name as string) || '—',
          remarks: (r.remarks as string) || '—',
        };
      })
      .sort((a, b) => b._sort.localeCompare(a._sort))
      .map((r, i) => ({ sno: i + 1, ...r })),
  };
}

export function allProjectsFinancialSection(
  projects: AllProjectsReportData['projects'],
  payments: Row[],
  purchases: Row[],
  labour: Row[],
  expenses: Row[],
): ReportSection {
  const totalBudget = projects.reduce((s, p) => s + Number(p.value_paise ?? 0), 0);
  const totalReceived = payments.reduce((s, r) => s + Number(r.amount_paise ?? 0), 0);
  const totalPending = Math.max(0, totalBudget - totalReceived);
  const materialCost = purchases.reduce((s, r) => s + Number(r.amount_paise ?? 0), 0);
  const advancePaid = purchases.reduce((s, r) => s + Number(r.advance_paid_paise ?? 0), 0);
  const supplierOutstanding = Math.max(0, materialCost - advancePaid);
  const labourCost = labour.reduce((s, r) => s + Number(r.total_amount_paise ?? 0), 0);
  const expenseCost = expenses.reduce((s, r) => s + Number(r.amount_paise ?? 0), 0);
  const totalSpent = materialCost + labourCost + expenseCost;
  const netPosition = totalReceived - totalSpent;

  const todayStr = day(new Date());
  let idx = 0;
  const line = (item: string, amount: number) => {
    idx += 1;
    return { sno: idx, date: todayStr, item, amount: formatMoney(amount), _amount: amount };
  };

  return {
    key: 'allFinancial',
    title: 'Consolidated Financial & Profitability Summary',
    icon: 'wallet',
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'item', label: 'Item' },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
    ],
    rows: [
      line('Total Portfolio Budget / Value', totalBudget),
      line('Total Collections Received from Clients', totalReceived),
      line('Total Pending Collections from Clients', totalPending),
      line('Total Material Purchases', materialCost),
      line('Advance Paid to Suppliers', advancePaid),
      line('Outstanding Payable to Suppliers', supplierOutstanding),
      line('Total Labour Cost', labourCost),
      line('Total Site Expenses', expenseCost),
      line('Total Project Expenditure (Materials + Labour + Expenses)', totalSpent),
      line('Net Portfolio Cash Position (Received − Spent)', netPosition),
    ],
  };
}

